/**
 * Adapts the pure engine to React. Every user action becomes one engine call; the
 * resulting `Transition` is folded into Flow, stats, persistence, sound and haptics, animation
 * cues and screen-reader announcements here — and nowhere in the components.
 *
 * State lives in a ref as well as React state so rapid typing always builds on the
 * latest snapshot, never a stale closure.
 */
import {
	backspace,
	encodeOutcomes,
	paceOffsets,
	solveOutcomes,
	checkAnswer,
	checkPuzzle,
	createGame,
	cursorCell,
	type DateKey,
	DEFAULT_LEVEL,
	formatDuration,
	type GameEvent,
	type GameState,
	isComplete,
	type Level,
	moveCursor,
	nextAnswer,
	pause,
	type PlayerStats,
	type PuzzleModel,
	recordBestFlow,
	recordCompletion,
	recordPracticeCompletion,
	recordStart,
	resume,
	revealAnswer,
	selectAnswer,
	selectCell,
	solveAll,
	toggleDirection,
	type Transition,
	typeLetter,
} from '@arrow/game-engine';
import { applyFlowEvents, createFlow, discountAway, type FlowLogEntry, type FlowState, tickFlow } from '@arrow/game-engine/flow';
import { applyNavigation, type NavigationDecision, rankNextAnswers } from '@arrow/game-engine/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type Prefs, prefs as appPrefs } from '../app/prefs.ts';
import { track } from '../platform/analytics.ts';
import { recordChallengeTaken, type TakenChallenge } from '../platform/challenges-taken.ts';
import { syncSolve, type SolveUpload } from '../platform/sync.ts';
import type { Persistence } from '../storage/persistence.ts';
import type { ChallengeContext } from './challenge.ts';
import { haptic, type HapticKind, prefersReducedMotion } from './haptics.ts';
import type { GameAction } from './keyboard.ts';
import { sound } from './sound.ts';
import { answerDescription, keyEcho, waveDistances } from './view.ts';

export interface GameSession {
	/**
	 * A daily counts towards stats/streak; a practice puzzle (Play) counts as a completed
	 * puzzle but never for the streak; a puzzle opened by id (dev) counts for nothing.
	 * Dailies and practice are tracked (analytics) and synced to the api; dev puzzles aren't.
	 */
	kind: 'daily' | 'practice' | 'puzzle';
	/** Storage slot for this attempt (see `slotFor`). */
	slot: string;
	puzzleId: string;
	/** The daily's date (the puzzle's date, not necessarily today). */
	dateKey: DateKey | null;
	/** `Sparrow No. n`, for dailies. */
	number: number | null;
	/** The daily's level (default `medium`), or the practice puzzle's. */
	level?: Level;
	/**
	 * Dailies played from a challenge link (`/c/:code`): who to beat. The solve carries
	 * its code, the masthead shows the pace ghost and the finish says who won.
	 */
	challenge?: ChallengeContext | null;
}

/** What useGame sends outside the device: injectable for tests. */
export interface GamePlatform {
	track: typeof track;
	syncSolve: (solve: SolveUpload) => unknown;
	/** Notes a finished challenge on this device (first finish per code), for Stats. */
	recordChallenge?: (taken: TakenChallenge) => unknown;
}

const defaultPlatform: GamePlatform = { track, syncSolve, recordChallenge: (t) => recordChallengeTaken(t) };

export type Phase = 'playing' | 'finishing' | 'card' | 'done';

/** Per-cell animation cues. Each cue carries the serial of the action that caused it. */
export interface CellFx {
	/** A letter was stamped into the cell. */
	pop?: number;
	/** A solved answer's sweep passes through: `i` letters from its start, along `axis`. */
	sweep?: { serial: number; i: number; axis: 'across' | 'down' };
	/** A solve handed this cell's letter to a crossing answer: `i` steps along the sweep. */
	cross?: { serial: number; i: number };
	/** Completion: `w` steps from the last letter, walking along answers. */
	wave?: { serial: number; w: number };
}

export interface Snapshot {
	game: GameState;
	flow: FlowState;
	stats: PlayerStats;
	log: FlowLogEntry[];
	phase: Phase;
	fx: Record<number, CellFx>;
	/** Wall-clock time each cue serial was created, so finished cues can be pruned. */
	fxAt: Record<number, number>;
	serial: number;
	/** The latest Flow hand-off, drawn on the board briefly (`n` = the chain's length). */
	links: { chain: string[]; serial: number; n: number } | null;
	/** Crossing answers whose arrows nudge after a solve, with each one's sweep step. */
	nudge: { serial: number; delays: Record<string, number> } | null;
	/** Spoken/visible feedback. Only `check` results are shown as text; solves and Flow
	 *  are shown on the board itself. */
	toast: { text: string; serial: number; kind: 'solve' | 'flow' | 'check' } | null;
	/** Polite screen-reader message. */
	announcement: string;
	/**
	 * The last change was a letter typed (or deleted) within the same answer: the cursor
	 * cell then has a short name ("Letter S, 2 of 3"), not the whole crossing (UX-033).
	 */
	typing: boolean;
	hint: boolean;
	/** The wave's longest step count, for timing the result card. */
	waveSteps: number;
	/**
	 * The last Flow-navigation decision (dev panel only): the answer whose completion
	 * triggered it and the scored candidates. Null until the first auto-move.
	 */
	nav: { completedId: string; decision: NavigationDecision } | null;
}

/** The completion beat, before the arrows fire (ms). Keep in step with `--beat` in board.css. */
export const FINISH_BEAT_MS = 650;

/** One moment of feel: its sound and its haptic together (both off-able in Settings, neither ever alone). */
function feel(kind: HapticKind, index?: number): void {
	sound(kind, index);
	haptic(kind);
}

export interface UseGameOptions {
	model: PuzzleModel;
	session: GameSession;
	/** The player's local date, or the dev override. */
	todayKey: DateKey;
	persistence: Persistence;
	/** Injectable clock for tests. */
	now?: () => number;
	/** Delay before the result card appears after the completion sequence. */
	finishDelayMs?: (waveSteps: number, arrows: number) => number;
	/**
	 * The big-board camera's frame: the cells currently on screen, so Flow navigation
	 * prefers a visible candidate on a near-tie and the camera only moves for a clearly
	 * better one. Omitted (or returning undefined) when the board shows whole.
	 */
	visibleCells?: (() => ReadonlySet<number> | undefined) | undefined;
	/** Analytics + solve sync (default: the real ones, which are off in unit tests). */
	platform?: GamePlatform;
	/**
	 * The answer to open on (Today's hand-over: the attract ghost's selection), if it's
	 * still open. Ignored on a finished puzzle.
	 */
	initialAnswer?: string | null | undefined;
	/** Where pauses are noted (default: the app's prefs, in localStorage). */
	prefs?: Pick<Prefs, 'pausedAt' | 'setPausedAt' | 'clearPausedAt'>;
}

const FX_TTL_MS = 4000;
const FLOW_TICK_MS = 5000;
/**
 * The completion sequence (board.css): a beat, the arrows fire in solve order (48ms
 * apart, 500ms each), the letters lift off along the wave (350ms, 14ms a step, capped),
 * then a short hold on the bare map before the card.
 */
export const defaultFinishDelay = (steps: number, arrows: number): number =>
	FINISH_BEAT_MS + arrows * 48 + 500 + 350 + Math.min(steps, 20) * 14 + 420;

function initialSnapshot(opts: UseGameOptions): Snapshot {
	const { model, session, persistence } = opts;
	let game = persistence.loadGame(model, session.slot) ?? createGame(model);
	const ask = opts.initialAnswer;
	if (ask && model.answers[ask] && !isComplete(game) && !game.solvedAnswers.includes(ask)) {
		game = selectAnswer(model, game, ask);
	}
	if (!game.cursor && !isComplete(game)) {
		const first = model.answerOrder.find((id) => !game.solvedAnswers.includes(id)) ?? model.answerOrder[0];
		if (first) game = selectAnswer(model, game, first);
	}
	return {
		game,
		flow: persistence.loadFlow(session.slot),
		stats: persistence.loadStats(),
		log: [],
		phase: isComplete(game) ? 'done' : 'playing',
		fx: {},
		fxAt: {},
		serial: 0,
		links: null,
		nudge: null,
		toast: null,
		announcement: '',
		typing: false,
		hint: false,
		waveSteps: 0,
		nav: null,
	};
}

export function useGame(opts: UseGameOptions) {
	const { model, session, todayKey, persistence } = opts;
	const now = opts.now ?? Date.now;
	const finishDelay = opts.finishDelayMs ?? defaultFinishDelay;

	const [snap, setSnap] = useState<Snapshot>(() => initialSnapshot(opts));
	const ref = useRef(snap);
	// Options read inside callbacks without re-creating them every render.
	const platform = opts.platform ?? defaultPlatform;
	const pauses = opts.prefs ?? appPrefs();
	const optsRef = useRef({ todayKey, now, finishDelay, visibleCells: opts.visibleCells, platform, pauses });
	optsRef.current = { todayKey, now, finishDelay, visibleCells: opts.visibleCells, platform, pauses };

	const commit = useCallback(
		(next: Snapshot) => {
			const prev = ref.current;
			if (next === prev) return;
			ref.current = next;
			setSnap(next);
			if (next.game !== prev.game) persistence.saveGame(session.slot, next.game, model);
			if (next.flow !== prev.flow) persistence.saveFlow(session.slot, next.flow);
			if (next.stats !== prev.stats) persistence.saveStats(next.stats);
		},
		[model, persistence, session.slot],
	);

	/** Folds an engine transition (or a plain navigation state) into the snapshot. */
	const apply = useCallback(
		(t: Transition, kind: 'input' | 'check' | 'reveal' | 'solveAll' = 'input') => {
			const prev = ref.current;
			if (t.state === prev.game && t.events.length === 0) return;
			const at = optsRef.current.now();
			const { events } = t;
			let game = t.state;

			const flowResult = applyFlowEvents(model, prev.flow, events);
			const flow = flowResult.flow;
			const log = flowResult.log.length ? [...prev.log, ...flowResult.log].slice(-200) : prev.log;

			// Stats: dailies only. Archive/dev plays are recorded against their own date with
			// today's key as completedOnDay, so the engine keeps them out of the streak.
			let stats = prev.stats;
			const level = session.level ?? DEFAULT_LEVEL;
			const started = prev.game.startedAt === null && game.startedAt !== null;
			const done = events.find((e): e is Extract<GameEvent, { type: 'puzzleCompleted' }> => e.type === 'puzzleCompleted');
			if (session.kind === 'daily' && session.dateKey && session.number !== null) {
				if (started) {
					stats = recordStart(stats, session.dateKey, {
						puzzleId: session.puzzleId,
						number: session.number,
						startedAt: game.startedAt!,
						level,
					});
				}
				// Keep the day's best Flow current mid-solve, not only on completion.
				if (flow.best > prev.flow.best) stats = recordBestFlow(stats, session.dateKey, flow.best, level);
				if (done) {
					stats = recordCompletion(
						stats,
						session.dateKey,
						{
							puzzleId: session.puzzleId,
							number: session.number,
							level,
							startedAt: game.startedAt ?? done.at,
							completedAt: done.at,
							elapsedMs: done.elapsedMs,
							checksUsed: game.checksUsed,
							revealsUsed: game.revealsUsed,
							bestFlow: flow.best,
						},
						optsRef.current.todayKey,
					);
				}
			} else if (session.kind === 'practice' && done) {
				stats = recordPracticeCompletion(stats, {
					elapsedMs: done.elapsedMs,
					checksUsed: game.checksUsed,
					revealsUsed: game.revealsUsed,
				});
			}
			// Analytics + solve sync, off the critical path. The dev-only solve-all isn't a play.
			if ((started || done) && kind !== 'solveAll') {
				report(optsRef.current.platform, session, level, optsRef.current.todayKey, game, flow, model, done ?? null);
			}

			const solved = events.filter((e): e is Extract<GameEvent, { type: 'answerSolved' }> => e.type === 'answerSolved');
			const completed = events.some((e) => e.type === 'puzzleCompleted');

			// Flow navigation: moving on after a solve is the UI's call. Only when the answer
			// under the cursor is the one solved (a crossing that happens to complete while
			// the player works elsewhere doesn't yank the cursor away). It then selects the
			// answer most likely to continue the Flow, ranked from what the player can see,
			// so rapid typing never lands on locked cells. It runs once, here, per solve: a
			// tap afterwards is a plain `navigate` and nothing re-selects over it.
			let nav = prev.nav;
			const cursorSolve = game.cursor ? solved.find((e) => e.answerId === game.cursor!.answerId) : undefined;
			if (!completed && cursorSolve) {
				const decision = rankNextAnswers(model, game, {
					solvedId: cursorSolve.answerId,
					opened: cursorSolve.opened,
					flowChain: flow.chain,
					visibleCells: optsRef.current.visibleCells?.(),
				});
				game = applyNavigation(model, game, decision);
				nav = { completedId: cursorSolve.answerId, decision };
			}

			// --- Animation cues ---------------------------------------------------------
			const serial = prev.serial + 1;
			const fx: Record<number, CellFx> = pruneFx(prev, at);
			const fxAt = liveSerials(fx, prev.fxAt);
			fxAt[serial] = at;
			const cue = (cell: number, patch: CellFx) => (fx[cell] = { ...fx[cell], ...patch });

			if (kind !== 'solveAll') {
				for (const e of events) if (e.type === 'letterEntered') cue(e.cell, { pop: serial });
			}
			let nudge = prev.nudge;
			const nudges: Record<string, number> = {};
			for (const e of solved) {
				const answer = model.answers[e.answerId]!;
				if (kind !== 'solveAll') answer.cells.forEach((cell, i) => cue(cell, { sweep: { serial, i, axis: answer.axis } }));
				// Crossing: each answer this solve handed a letter to pulses at the shared
				// cell as the sweep passes it, and its arrow nudges.
				for (const openedId of e.opened) {
					const i = answer.cells.findIndex((c) => model.answers[openedId]!.cells.includes(c));
					if (i < 0 || isComplete(game)) continue;
					cue(answer.cells[i]!, { cross: { serial, i } });
					nudges[openedId] = i;
				}
			}
			if (Object.keys(nudges).length) nudge = { serial, delays: nudges };
			let waveSteps = prev.waveSteps;
			if (completed) {
				const lastTyped = [...events].reverse().find((e) => e.type === 'letterEntered');
				const origin =
					(lastTyped && lastTyped.type === 'letterEntered' ? lastTyped.cell : null) ??
					cursorCell(model, prev.game) ??
					model.letterCells[0]!;
				const dist = waveDistances(model, origin);
				waveSteps = Math.max(0, ...dist.values());
				for (const [cell, w] of dist) cue(cell, { wave: { serial, w } });
			}

			const continued = flowResult.log.some((l) => l.kind === 'continue');
			const links = continued && flow.chain.length > 1 ? { chain: [...flow.chain], serial, n: flow.current } : prev.links;

			// --- Feedback --------------------------------------------------------------
			let toast = prev.toast;
			let announcement = prev.announcement;
			const typedSolves = solved.filter((e) => e.source !== 'solveAll');
			if (typedSolves.length > 0 && !completed) {
				const flowing = continued && flow.current > 1;
				toast = { text: flowing ? `Flow ×${flow.current}` : 'Solved', serial, kind: flowing ? 'flow' : 'solve' };
				const names = typedSolves.map((e) => model.answers[e.answerId]!.text).join(', ');
				announcement = typedSolves.every((e) => e.source === 'reveal')
					? `Revealed ${names}.`
					: `Solved ${names}.${continued ? ` Flow times ${flow.current}.` : ''}`;
			}
			const flagged = events.find((e): e is Extract<GameEvent, { type: 'cellsFlagged' }> => e.type === 'cellsFlagged');
			if (flagged) {
				const n = flagged.cells.length;
				const text = n === 0 ? 'Nothing wrong so far' : `${n} wrong ${n === 1 ? 'letter' : 'letters'}`;
				toast = { text, serial, kind: 'check' };
				announcement = `${text}.`;
			}
			if (completed) announcement = `Solved in ${formatDuration(game.elapsedMs)}.`;
			else if (game.cursor && game.cursor.answerId !== prev.game.cursor?.answerId && !flagged) {
				const describe = answerDescription(model, game.cursor.answerId);
				announcement = typedSolves.length ? `${announcement} Next: ${describe}` : describe;
			}
			// Something to say this time (not just a letter typed): say it even if it's the same words.
			const moved = !!game.cursor && game.cursor.answerId !== prev.game.cursor?.answerId;
			const spoke = completed || !!flagged || typedSolves.length > 0 || moved;

			// --- Sound and haptics -------------------------------------------------------
			// (Completion's celebration lands when the arrows fire, after the beat; with
			// reduced motion there's no beat to wait for, so it plays now.)
			const reduced = prefersReducedMotion();
			if (completed) {
				feel('solved');
				if (reduced) feel('completed');
			} else if (continued && flow.current > 1 && typedSolves.length) feel('flow');
			else if (typedSolves.length) feel('solved');
			else if (flagged && flagged.cells.length > 0) feel('wrong');

			const phase: Phase = completed ? (reduced ? 'card' : 'finishing') : prev.phase;

			commit({
				...prev,
				game,
				flow,
				stats,
				log,
				fx,
				fxAt,
				serial,
				links,
				nudge,
				toast,
				announcement: spoke ? fresh(prev.announcement, announcement) : prev.announcement,
				typing: kind === 'input' && !completed && !moved && !!game.cursor,
				phase,
				waveSteps,
				nav,
				hint: prev.hint && !events.some((e) => e.type === 'letterEntered'),
			});
		},
		[commit, model, session],
	);

	/** Navigation-only changes (selection): no events, no cues. */
	const navigate = useCallback(
		(game: GameState) => {
			const prev = ref.current;
			if (game === prev.game) return;
			const changed = game.cursor?.answerId !== prev.game.cursor?.answerId;
			commit({
				...prev,
				game,
				typing: false,
				announcement: changed && game.cursor ? fresh(prev.announcement, answerDescription(model, game.cursor.answerId)) : prev.announcement,
			});
		},
		[commit, model],
	);

	/**
	 * One action. `echo` (the drawn keyboard): if nothing else was said, say what the key did
	 * ("S, 2 of 5", "Deleted, 3 of 5", the answer after a switch), since a screen reader
	 * stays on the key and a phone has no keyboard echo of its own (UX-033).
	 */
	const dispatch = useCallback(
		(action: GameAction, opts: { echo?: boolean } = {}) => {
			const before = ref.current;
			const s = before.game;
			const t = optsRef.current.now();
			switch (action.type) {
				case 'letter': {
					if (isComplete(s)) return;
					feel('key', s.cursor?.index ?? 0);
					apply(typeLetter(model, s, action.letter, t));
					break;
				}
				case 'backspace':
					if (isComplete(s)) return;
					apply(backspace(model, s, t));
					// Pitched by the cell it cleared (where the cursor now sits).
					feel('del', ref.current.game.cursor?.index ?? 0);
					break;
				case 'move':
					navigate(moveCursor(model, s, action.dir));
					break;
				case 'nextAnswer':
					navigate(nextAnswer(model, s, action.delta));
					break;
				case 'toggleDirection':
					navigate(toggleDirection(model, s));
					break;
			}
			if (!opts.echo) return;
			const after = ref.current;
			if (after.announcement !== before.announcement) return; // a solve, a move: already said
			const text = keyEcho(model, action, before.game, after.game);
			if (text) commit({ ...after, announcement: fresh(after.announcement, text) });
		},
		[apply, commit, model, navigate],
	);

	const showHintOnce = useCallback(() => {
		if (persistence.hintSeen()) return;
		persistence.markHintSeen();
		commit({ ...ref.current, hint: true });
	}, [commit, persistence]);

	const tapCell = useCallback(
		(cell: number) => {
			navigate(selectCell(model, ref.current.game, cell));
			if (!isComplete(ref.current.game)) showHintOnce();
		},
		[model, navigate, showHintOnce],
	);

	/** A tap on one clue (half of a clue cell): select its answer; tap it again to toggle. */
	const tapClue = useCallback(
		(clueId: string) => {
			const clue = model.clues[clueId];
			if (!clue) return;
			const s = ref.current.game;
			const cell = clue.cell.row * model.width + clue.cell.col;
			if (!isComplete(s)) feel('clue');
			navigate(s.cursor?.answerId === clue.answerId ? selectCell(model, s, cell) : selectAnswer(model, s, clue.answerId));
			if (!isComplete(ref.current.game)) showHintOnce();
		},
		[model, navigate, showHintOnce],
	);

	const selectAnswerId = useCallback(
		(answerId: string) => navigate(selectAnswer(model, ref.current.game, answerId)),
		[model, navigate],
	);

	const check = useCallback(
		(scope: 'answer' | 'puzzle') => {
			const s = ref.current.game;
			const t = optsRef.current.now();
			apply(scope === 'answer' ? checkAnswer(model, s, t) : checkPuzzle(model, s, t), 'check');
		},
		[apply, model],
	);

	const reveal = useCallback(() => {
		apply(revealAnswer(model, ref.current.game, optsRef.current.now()), 'reveal');
	}, [apply, model]);

	const completeAll = useCallback(() => {
		apply(solveAll(model, ref.current.game, optsRef.current.now()), 'solveAll');
	}, [apply, model]);

	const reset = useCallback(() => {
		const game = createGame(model);
		const first = model.answerOrder[0];
		commit({
			...ref.current,
			game: first ? selectAnswer(model, game, first) : game,
			flow: createFlow(),
			log: [],
			phase: 'playing',
			fx: {},
			fxAt: {},
			links: null,
			nudge: null,
			toast: null,
			waveSteps: 0,
			nav: null,
			announcement: 'Puzzle reset.',
		});
	}, [commit, model]);

	const dismissHint = useCallback(() => {
		if (ref.current.hint) commit({ ...ref.current, hint: false });
	}, [commit]);

	const openCard = useCallback(() => {
		if (isComplete(ref.current.game)) commit({ ...ref.current, phase: 'card' });
	}, [commit]);

	const closeCard = useCallback(() => {
		if (ref.current.phase === 'card' || ref.current.phase === 'finishing') commit({ ...ref.current, phase: 'done' });
	}, [commit]);

	/** Replace stats from outside (dev tools). */
	const replaceStats = useCallback((stats: PlayerStats) => commit({ ...ref.current, stats }), [commit]);

	// --- Clock and visibility ---------------------------------------------------------
	// Leaving the game (Today, a tab, any route change: the unmount), hiding the page or
	// closing it pauses the clock and saves at once, and notes when. Coming back resumes
	// exactly where it was, and the time away is taken out of Flow's inactivity window
	// (the chain's last solve moves forward by it), so a break never costs a Flow.
	const pauseClock = useCallback(() => {
		const prev = ref.current;
		const at = optsRef.current.now();
		const game = pause(prev.game, at);
		if (game !== prev.game) commit({ ...prev, game });
		else persistence.saveGame(session.slot, prev.game, model);
		if (game.startedAt !== null && game.completedAt === null) optsRef.current.pauses.setPausedAt(session.slot, at);
	}, [commit, model, persistence, session.slot]);

	const wake = useCallback(() => {
		const prev = ref.current;
		const t = optsRef.current.now();
		const { pauses } = optsRef.current;
		const pausedAt = pauses.pausedAt(session.slot);
		let flow = prev.flow;
		if (pausedAt !== null) {
			pauses.clearPausedAt(session.slot);
			flow = discountAway(flow, pausedAt, t);
		}
		const game = resume(prev.game, t);
		const ticked = tickFlow(flow, t);
		if (game === prev.game && ticked.flow === prev.flow) return;
		commit({ ...prev, game, flow: ticked.flow, log: ticked.log.length ? [...prev.log, ...ticked.log] : prev.log });
	}, [commit, session.slot]);

	useEffect(() => {
		const onVisibility = () => (document.visibilityState === 'hidden' ? pauseClock() : wake());
		if (document.visibilityState === 'visible') wake();
		document.addEventListener('visibilitychange', onVisibility);
		window.addEventListener('pagehide', pauseClock);
		return () => {
			document.removeEventListener('visibilitychange', onVisibility);
			window.removeEventListener('pagehide', pauseClock);
			pauseClock();
		};
	}, [pauseClock, wake]);

	// Stale Flow chains fade out without waiting for the next solve.
	useEffect(() => {
		const id = window.setInterval(() => {
			const prev = ref.current;
			const ticked = tickFlow(prev.flow, optsRef.current.now());
			if (ticked.flow !== prev.flow) commit({ ...prev, flow: ticked.flow, log: [...prev.log, ...ticked.log] });
		}, FLOW_TICK_MS);
		return () => window.clearInterval(id);
	}, [commit]);

	// The completion sequence plays on the board (the celebration's sound and strongest
	// haptic as the arrows fire), then the result card takes over.
	useEffect(() => {
		if (snap.phase !== 'finishing') return;
		const fire = window.setTimeout(() => feel('completed'), FINISH_BEAT_MS);
		const id = window.setTimeout(() => {
			if (ref.current.phase === 'finishing') commit({ ...ref.current, phase: 'card' });
		}, optsRef.current.finishDelay(snap.waveSteps, model.answerOrder.length));
		return () => {
			window.clearTimeout(fire);
			window.clearTimeout(id);
		};
	}, [commit, model, snap.phase, snap.waveSteps]);

	// The first-time hint dismisses itself.
	useEffect(() => {
		if (!snap.hint) return;
		// One beat: it sits in the clue band's place, so the clue comes back soon (UX-035).
		const id = window.setTimeout(dismissHint, 4000);
		return () => window.clearTimeout(id);
	}, [dismissHint, snap.hint]);

	const actions = useMemo(
		() => ({
			dispatch,
			tapCell,
			tapClue,
			selectAnswer: selectAnswerId,
			check,
			reveal,
			solveAll: completeAll,
			reset,
			dismissHint,
			openCard,
			closeCard,
			replaceStats,
		}),
		[dispatch, tapCell, tapClue, selectAnswerId, check, reveal, completeAll, reset, dismissHint, openCard, closeCard, replaceStats],
	);

	return { snap, actions };
}

export type GameActions = ReturnType<typeof useGame>['actions'];

/**
 * Tracks the start / completion of a daily or practice puzzle and uploads the solve.
 * Fire-and-forget: the platform functions queue and never throw, and a failure here
 * must never reach the game.
 */
function report(
	platform: GamePlatform,
	session: GameSession,
	level: Level,
	todayKey: DateKey,
	game: GameState,
	flow: FlowState,
	model: PuzzleModel,
	done: Extract<GameEvent, { type: 'puzzleCompleted' }> | null,
): void {
	try {
		if (session.kind === 'puzzle') return;
		const bestFlow = flow.best;
		// A finished solve's shape (how each cell fell) and pace: spoiler-free, for the share,
		// the challenge card and the pace ghost.
		let shape: { cells?: string; paceMs?: number[] } = {};
		if (done) {
			try {
				const pace = paceOffsets(game).slice(0, 200);
				shape = { cells: encodeOutcomes(solveOutcomes(model, game, flow), model.width), ...(pace.length ? { paceMs: pace } : {}) };
			} catch {
				shape = {};
			}
		}
		const challenge = session.kind === 'daily' ? (session.challenge ?? null) : null;
		const mistakes = game.mistakes ?? 0;
		const startedAt = new Date(game.startedAt ?? done?.at ?? Date.now()).toISOString();
		const counters = { checksUsed: game.checksUsed, revealsUsed: game.revealsUsed, mistakes, bestFlow };
		const finish = done
			? {
					status: game.revealsUsed > 0 ? ('revealed' as const) : ('completed' as const),
					completedAt: new Date(done.at).toISOString(),
					durationMs: Math.max(0, Math.round(done.elapsedMs)),
				}
			: { status: 'started' as const };

		if (session.kind === 'daily') {
			if (!session.dateKey || session.number === null) return;
			const isToday = session.dateKey === todayKey;
			const daily = { number: session.number, level, isToday };
			if (done) {
				platform.track('daily_completed', {
					...daily,
					durationMs: finish.durationMs ?? 0,
					checks: game.checksUsed,
					reveals: game.revealsUsed,
					mistakes,
					bestFlow,
					onTime: isToday,
				});
			} else platform.track('daily_started', daily);
			if (challenge) {
				const props = { code: challenge.code, number: challenge.number, level: challenge.level };
				if (done) {
					const ms = finish.durationMs ?? 0;
					const beat = ms < challenge.durationMs;
					platform.track('challenge_completed', {
						...props,
						beat,
						durationMs: ms,
						marginMs: Math.abs(ms - challenge.durationMs),
					});
					// Kept on the device so Stats can say how you do against challengers. Anonymous.
					platform.recordChallenge?.({
						code: challenge.code,
						number: challenge.number,
						level: challenge.level,
						date: challenge.date,
						yourMs: ms,
						theirMs: challenge.durationMs,
						beat,
						finishedAt: new Date(done.at).toISOString(),
					});
				} else platform.track('challenge_started', props);
			}
			void platform.syncSolve({
				puzzleId: session.puzzleId,
				kind: 'daily',
				level,
				dailyDate: session.dateKey,
				dailyNumber: session.number,
				startedAt,
				...counters,
				...finish,
				...shape,
				...(done ? { onTime: isToday } : {}),
				...(challenge ? { challengeCode: challenge.code } : {}),
			});
			return;
		}

		if (done) {
			platform.track('practice_completed', {
				puzzleId: session.puzzleId,
				level,
				durationMs: finish.durationMs ?? 0,
				checks: game.checksUsed,
				reveals: game.revealsUsed,
				mistakes,
				bestFlow,
			});
		} else platform.track('practice_started', { puzzleId: session.puzzleId, level });
		void platform.syncSolve({ puzzleId: session.puzzleId, kind: 'practice', startedAt, ...counters, ...finish, ...shape });
	} catch {
		// Never let analytics break play.
	}
}

/** Keeps the timestamps of serials some cue still refers to. */
function liveSerials(fx: Record<number, CellFx>, fxAt: Record<number, number>): Record<number, number> {
	const out: Record<number, number> = {};
	const keep = (serial: number | undefined) => {
		if (serial !== undefined && fxAt[serial] !== undefined) out[serial] = fxAt[serial];
	};
	for (const cue of Object.values(fx)) {
		keep(cue.pop);
		keep(cue.sweep?.serial);
		keep(cue.cross?.serial);
		keep(cue.wave?.serial);
	}
	return out;
}

function pruneFx(prev: Snapshot, at: number): Record<number, CellFx> {
	const alive = (serial: number | undefined) => serial !== undefined && at - (prev.fxAt[serial] ?? 0) < FX_TTL_MS;
	const out: Record<number, CellFx> = {};
	for (const [key, cue] of Object.entries(prev.fx)) {
		const kept: CellFx = {};
		if (alive(cue.pop)) kept.pop = cue.pop;
		if (cue.sweep && alive(cue.sweep.serial)) kept.sweep = cue.sweep;
		if (cue.cross && alive(cue.cross.serial)) kept.cross = cue.cross;
		// The completion wave stays: it is the finished grid's resting state.
		if (cue.wave) kept.wave = cue.wave;
		const same = kept.pop === cue.pop && kept.sweep === cue.sweep && kept.cross === cue.cross && kept.wave === cue.wave;
		if (same) out[Number(key)] = cue;
		else if (Object.keys(kept).length) out[Number(key)] = kept;
	}
	return out;
}

/**
 * A live region only speaks when its text changes, so the same words twice in a row (a
 * second Check with nothing wrong, coming back to the same clue) would be silent. A
 * trailing no-break space makes the repeat a change; screen readers don't voice it.
 */
export function reannounce(text: string): string {
	return text.endsWith('\u00a0') ? text.slice(0, -1) : `${text}\u00a0`;
}

/** `next`, made to speak again if it's what the region already says. */
export function fresh(prev: string, next: string): string {
	return next === prev.replace(/\u00a0$/, '') ? reannounce(prev) : next;
}
